import React, { useState, useEffect } from "react"
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Alert,
  Label,
  Input,
  Badge,
  FormGroup,
  Button,
} from "reactstrap"
import { post, get } from "../../helpers/api_helper"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { Trans } from "react-i18next"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import Loader from "common/loader"
import Select from "react-select"
import { PARTNERS_BRANCH_SEARCH_TIMESLOT } from "helpers/api_endpoints"
import { ITEMS_PER_PAGE } from "helpers/pagination_helper"

//meta title
document.title = "Partner Branch Time Slots"

const PartnersBranchTimeSlotList = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [timeSlots, setTimeSlots] = useState([])
  const [filteredTimeSlots, setFilteredTimeSlots] = useState([])
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [successMsg, setsuccessMsg] = useState("")
  const [errorMsg, seterrorMsg] = useState('')
  const [partnerBranchName, setPartnerBranchName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (location.state) {
        if (location.state.successMsg) {
            setsuccessMsg(location.state.successMsg);
            setTimeout(() => {
                setsuccessMsg("");
                navigate(location.pathname, { replace: true, state: {} });
            }, 4000);
        }
        if (location.state.errorMsg) {
            seterrorMsg(location.state.errorMsg);
            setTimeout(() => {
                seterrorMsg("");
                navigate(location.pathname, { replace: true, state: {} });
            }, 4000);
        }
    }
    
    fetchTimeSlots()
  }, [page, filters, location.state, navigate, location.pathname])

  useEffect(() => {
    // Apply search filter whenever searchQuery or timeSlots changes
    if (searchQuery) {
      const filtered = timeSlots.filter(slot => 
        String(slot.id).includes(searchQuery) ||
        (slot?.partnerBranch?.name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (slot.startTime?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (slot.endTime?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (slot.type?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (slot.recurringDays?.some(day => 
          getDayName(day).toLowerCase().includes(searchQuery.toLowerCase())
        )))
      setFilteredTimeSlots(filtered)
    } else {
      setFilteredTimeSlots(timeSlots)
    }
  }, [searchQuery, timeSlots])

  const fetchTimeSlots = () => {
    setLoading(true)
    get(PARTNERS_BRANCH_SEARCH_TIMESLOT)
      .then(response => {
          const data = response.data;
          setTimeSlots(data);
          setFilteredTimeSlots(data); // Initialize filtered data with all time slots
          setTotalPages(data.pagination?.totalPages || 1);
          setPage(data.pagination?.currentPage || 1);
          setLoading(false);
      })
      .catch(error => {
          if (error?.response?.status == 400) {
              console.log(error)
          }
          setLoading(false)
      })
  }

  const handleSearch = (e) => {
    e.preventDefault()
    // No need to setPage(1) here since we're doing client-side filtering
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    // No need to setPage(1) here since we're doing client-side filtering
  }

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }

  const getDayName = (dayNumber) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days[dayNumber] || dayNumber
  }

  const formatTime = (timeString) => {
    if (!timeString) return ''
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours, 10)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes} ${ampm}`
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs 
            title="Home" 
            breadcrumbItem="Partners Branch Time Slot"
          />
          <Row>
            <Col xl={12}>
              <Card>
                <br></br>
                {successMsg ? (
                  <Alert style={{ margin: "25px" }} color="success">
                    {successMsg}
                  </Alert>
                ) : null}
                
                {/* Search Box */}
                <CardBody>
                  <form onSubmit={handleSearch}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="search">Search</Label>
                          <div className="d-flex">
                            <Input
                              type="text"
                              id="search"
                              name="search"
                              placeholder="Search by partner id, partner name, time slot, etc..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Button 
                              type="submit" 
                              color="primary" 
                              className="ms-2"
                            >
                              <i className="fas fa-search"></i>
                            </Button>
                            {searchQuery && (
                              <Button 
                                color="secondary" 
                                className="ms-2"
                                onClick={handleClearSearch}
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </form>
                </CardBody>

                <CardTitle>
                  <Link to={`/partnersbranch/timeslot/add`}>
                    <button
                      style={{ float: "right", marginRight: "20px" }}
                      className="btn btn-sm btn-primary"
                    >
                      <i className="dripicons-plus"></i> &nbsp;
                      <Trans>Create Time Slot</Trans>
                    </button>
                  </Link>
                </CardTitle>

                <CardBody>
                {errorMsg ? <Alert color="danger">{errorMsg}</Alert> : null}
                  <div className="table-responsive">
                    <Table className="table table-striped table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>
                            <Trans>ID</Trans>
                          </th>
                          <th>
                            <Trans>Partner Name</Trans>
                          </th>
                          <th>
                            <Trans>Time Slot</Trans>
                          </th>
                          <th>
                            <Trans>Cut Off Time</Trans>
                          </th>
                          <th>
                            <Trans>Order Start Time</Trans>
                          </th>
                          <th>
                            <Trans>Expected Orders</Trans>
                          </th>
                          <th>
                            <Trans>Type</Trans>
                          </th>
                          <th>
                            <Trans>Recurring Days</Trans>
                          </th>
                          <th>
                            <Trans>Status</Trans>
                          </th>
                          <th>#</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <th colSpan={9} className="text-center">
                              <Loader></Loader>
                            </th>
                          </tr>
                        ) : null}
                        {filteredTimeSlots?.length === 0 && !loading ? (
                          <tr>
                            <td colSpan={9} className="text-center">
                              <Trans>No time slots found</Trans>
                              {searchQuery && (
                                <div>
                                  <Trans>for search term:</Trans> "{searchQuery}"
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                        {filteredTimeSlots?.map((timeSlot, index) => (
                          <tr key={index}>
                            <td>{timeSlot.id}</td>
                            <td>{timeSlot?.partnerBranch?.name}</td>
                            <td>
                              {formatTime(timeSlot.startTime)} - {formatTime(timeSlot.endTime)}
                            </td>
                            <td>{formatTime(timeSlot.cutOffTime)}</td>
                            <td>{formatTime(timeSlot.orderStartTime)}</td>
                            <td>{timeSlot.expectedOrders}</td>
                            <td>
                              <Badge className="bg-info text-capitalize">
                                {timeSlot.type}
                              </Badge>
                            </td>
                            <td>
                              {timeSlot.recurringDays?.map(day => getDayName(day)).join(', ')}
                            </td>
                            <td>
                              <Badge
                                className={
                                  timeSlot.status == 2
                                    ? "bg-danger"
                                    : "bg-success"
                                }
                              >
                                <Trans>
                                  {timeSlot.status == 2
                                    ? "inactive"
                                    : "active"}
                                </Trans>
                              </Badge>
                            </td>
                            <td>
                              <Link to={`/partnersbranch/timeslot/edit/${timeSlot.id}`}>
                                <button className="btn btn-sm btn-info">
                                  <i className="dripicons-pencil"></i> &nbsp;
                                  <Trans>Edit</Trans>
                                </button>
                              </Link>{" "}
                              &nbsp;
                              <Link to={`/partnersbranch/timeslot/remove/${timeSlot.id}`}>
                                <button className="btn btn-sm btn-danger">
                                  <i className="dripicons-trash"></i> &nbsp;
                                  <Trans>Remove</Trans>{" "}
                                </button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <br></br>
                  {totalPages > 1 && (
                    <div className="text-center">
                      <button
                        className="btn btn-sm btn-dark"
                        onClick={handlePrevPage}
                        disabled={page === 1}
                      >
                        <Trans>Previous</Trans>
                      </button>
                      &nbsp;
                      <button
                        className="btn btn-sm btn-dark"
                        onClick={handleNextPage}
                        disabled={page === totalPages}
                      >
                        <Trans>Next</Trans>
                      </button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  )
}

export default PartnersBranchTimeSlotList